import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { disableSendDefaultMessage, setEditableMessage } from '../../actions/chat/actions';
import { Captcha } from '../../components/Captcha';
import { MessageInputField } from '../../components/MessageInputField';
import { ReplyTemplateButton } from '../../components/ReplyTemplateButton';
import { BLOB_ID } from '../../constants/blobId';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { useReplyTemplateModal } from '../../hooks';
import { useSendMessage } from '../../hooks/useSendMessage/useSendMessage';
import {
  chatbotSelector,
  chatListSelector,
  chatWindowSelector,
  currentChatOpponentIdSelector,
  currentOpenChatSelector,
  hostTypeSelector,
  isShowSubAgentChatListSelector,
  offerSelector,
  selectActiveReplyMessage,
} from '../../selectors';
import { hintQuestionsIsVisibleSelector } from '../../selectors/hintQuestionsSelector';
import { TAnalyticsKey } from '../../types/chatHint';
import { EFirstMessageStatus } from '../../types/firstMessageStatus';
import { EHostType } from '../../types/hostType';
import { ERoute } from '../../types/route';
import { AttachFileButtonContainer } from '../AttachFileButtonContainer';
import { ChatbotMenuContainer } from '../ChatbotMenuContainer';
import { HintQuestionsContainer } from '../HintQuestionsContainer';
import { EReplyTemplateTrackingSource, trackReplyTemplateButtonClick } from '../ReplyTemplateModalContainer/tracking';
import { useRuntimeSetting } from '../../hooks/useRuntimeSetting';
import { ReplyPreviewContainer } from '../ReplyPreviewContainer';

interface IMessageInputFieldContainerProps {
  onHeightChange(): void;
}

export const MessageInputFieldContainer: React.FC<IMessageInputFieldContainerProps> = ({
  onHeightChange,
}: IMessageInputFieldContainerProps) => {
  const dispatch = useDispatch();
  const offer = useSelector(offerSelector);
  const currentChat = useSelector(currentOpenChatSelector);
  const hostType = useSelector(hostTypeSelector);
  const { isBubbleInputFocused } = useSelector(chatWindowSelector);
  const isShowSubAgentChatList = useSelector(isShowSubAgentChatListSelector);
  const chatList = useSelector(chatListSelector);
  const hintQuestionsIsVisible = useSelector(hintQuestionsIsVisibleSelector);
  const { isTyping, messageSending: chatbotMessageSending } = useSelector(chatbotSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);
  const activeReplyMessage = useSelector(selectActiveReplyMessage);

  const { chatId, availableActions, firstMessageStatus, editableMessage, sendDefaultMessage, defaultMessagePayload } =
    currentChat || {};
  const { id: offerId } = offer || {};
  const { onSend, setAnalyticsKey } = useSendMessage();
  const {
    isReplyTemplateEnabled,
    isReplyTemplateNewEnabledInputButton,
    isReplyTemplateInputButtonVisible,
    openReplyTemplateModal,
  } = useReplyTemplateModal();

  const isChatbotGPTEnabled = useRuntimeSetting<boolean>('frontend-chats.chatbotGPT.Enabled', false);

  const isIframe = hostType === EHostType.Frame;

  const isFileUploadButtonEnabled = useRuntimeSetting<boolean>('frontend-chats.FileUploadButton.Enabled', false);
  const isAttachButtonEnabled = isFileUploadButtonEnabled && availableActions?.includes('sendFile');
  const isSendMessageEnabled =
    (isIframe && hintQuestionsIsVisible) ||
    (isIframe && typeof availableActions === 'undefined') ||
    (isIframe && availableActions === null) ||
    (hostType === EHostType.Page && chatId === BLOB_ID && availableActions === null) ||
    availableActions?.includes('sendMessage') ||
    chatId === CHATBOT_ID ||
    defaultMessagePayload;
  const isShowCaptcha = firstMessageStatus === EFirstMessageStatus.NeedCaptcha;

  const onHintQuestionChange = React.useCallback(
    (analyticsKey: TAnalyticsKey) => {
      setAnalyticsKey(analyticsKey);
    },
    [setAnalyticsKey],
  );

  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (!activeReplyMessage) {
      return;
    }

    inputRef.current?.focus();
  }, [activeReplyMessage]);

  const handleReplyTemplateClick = React.useCallback(() => {
    trackReplyTemplateButtonClick({ chatId, opponentId, source: EReplyTemplateTrackingSource.TextField });
    openReplyTemplateModal(EReplyTemplateTrackingSource.TextField);
  }, [chatId, openReplyTemplateModal, opponentId]);

  const leftButton = React.useMemo(() => {
    if (chatId === CHATBOT_ID && isChatbotGPTEnabled) {
      return <ChatbotMenuContainer />;
    }

    return isAttachButtonEnabled ? <AttachFileButtonContainer /> : null;
  }, [isAttachButtonEnabled, chatId, isChatbotGPTEnabled]);

  const replyTemplateButton = React.useMemo(
    () =>
      isReplyTemplateEnabled && isReplyTemplateInputButtonVisible ? (
        <ReplyTemplateButton isNewEnabled={isReplyTemplateNewEnabledInputButton} onClick={handleReplyTemplateClick} />
      ) : null,
    [
      isReplyTemplateEnabled,
      isReplyTemplateInputButtonVisible,
      isReplyTemplateNewEnabledInputButton,
      handleReplyTemplateClick,
    ],
  );

  React.useEffect(() => {
    if (editableMessage && sendDefaultMessage && chatId) {
      dispatch(disableSendDefaultMessage({ chatId }));
      if (chatId !== BLOB_ID) {
        dispatch(setEditableMessage({ chatId, text: '' }));
      }
      onSend({ text: editableMessage, chatId, payload: defaultMessagePayload });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, offerId, editableMessage, sendDefaultMessage]);

  if (
    !chatId ||
    isBubbleInputFocused ||
    chatList.currentRoute === ERoute.Spam ||
    (chatId === CHATBOT_ID && !isChatbotGPTEnabled)
  ) {
    return null;
  }

  return (
    <MessageInputField
      replyQuote={<ReplyPreviewContainer />}
      hintQuestions={<HintQuestionsContainer onHintQuestionChange={onHintQuestionChange} />}
      chatId={chatId}
      isLoading={firstMessageStatus === EFirstMessageStatus.Pending || chatbotMessageSending}
      isInputDisabled={!isSendMessageEnabled}
      isSendButtonDisabled={!isSendMessageEnabled || isTyping}
      onSend={onSend}
      leftButton={leftButton}
      captcha={isShowCaptcha && <Captcha chatId={chatId} />}
      onHeightChange={onHeightChange}
      placeholder={
        isShowSubAgentChatList
          ? 'Написать сообщение от лица агента'
          : chatId === CHATBOT_ID && isChatbotGPTEnabled
            ? 'Напишите, что вы ищете'
            : ''
      }
      replyTemplateButton={replyTemplateButton}
      inputRef={inputRef}
    />
  );
};
